// src/services/assets/assetTransactionService.js

/**
 * =====================================================
 * ASSET TRANSACTION SERVICE
 * =====================================================
 *
 * PURPOSE:
 * - Single service for all asset custody movements
 * - Workflow routing via resolveWorkflow only
 * - All state transitions via validateWorkflowTransition
 * - Replay-safe ledger writes
 * - Multi-tenant enforced everywhere
 *
 * APPROVED FLOWS:
 * ─────────────────────────────────────────────────────
 * STORE → STAFF/TECHNICIAN/EMPLOYEE/SITE/STORE  DIRECT FINAL
 * STAFF/SITE/TECHNICIAN → STORE                 RETURN APPROVAL
 * STAFF/TECHNICIAN/SITE (any peer)              ACCEPTANCE REQUIRED
 * ─────────────────────────────────────────────────────
 *
 * RULES:
 * - No raw status strings anywhere
 * - No workflow logic in UI
 * - No holder mutation without FINALIZED state
 * - No transition without validateWorkflowTransition
 * - company_id enforced on every operation
 *
 * CHANGES vs original:
 *   1. confirmHandOver() — NEW: sender confirms physical handover
 *      Sets sender_handed_over: true on transaction
 *      No workflow state change — custody pending until receiver accepts
 *   2. cancelTransfer()  — ADDITIVE: cancellation_reason + cancelled_by_role
 *      Backward compatible — reason is optional at service layer
 *      UI enforces mandatory reason for sender refusals
 * =====================================================
 */

import {
    collection,
    doc,
    getDoc,
    setDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    runTransaction,
    serverTimestamp,
} from "firebase/firestore";

import { db } from "../../firebase";

import { MOVEMENT_TYPES }  from "../../constants/enums/movementTypes";

import { resolveWorkflow }    from "./workflowResolver";
import { WORKFLOW_TYPES }     from "./workflowTypes";
import { WORKFLOW_STATES, ACTIVE_PENDING_WORKFLOW_STATES } from "./workflowStates";
import { TRANSACTION_CLASSES } from "./transactionClasses";
import { WORKFLOW_BUCKETS }    from "./workflowBuckets";

import {
    validateWorkflowTransition,
} from "../../engines/workflow/validateWorkflowTransition";

import { createNotification } from "../notificationService";

// =====================================================
// COLLECTIONS
// =====================================================

const ASSET_MASTER_COLLECTION      = "asset_master";
const ASSET_TRANSACTION_COLLECTION = "asset_transactions";

// =====================================================
// HOLDER TYPES
// =====================================================

export const HOLDER_TYPE = {
    STORE:      "STORE",
    STAFF:      "STAFF",
    TECHNICIAN: "TECHNICIAN",
    EMPLOYEE:   "EMPLOYEE",
    SITE:       "SITE",
};

// =====================================================
// INTERNAL HELPERS — unchanged
// =====================================================

const validateCompany = (companyId) => {
    if (!companyId) throw new Error("company_id is required");
};

const validateAsset = async (assetId, companyId) => {
    const assetRef  = doc(db, ASSET_MASTER_COLLECTION, assetId);
    const assetSnap = await getDoc(assetRef);
    if (!assetSnap.exists()) throw new Error("Asset not found");
    const assetData = assetSnap.data();
    if (assetData.company_id !== companyId) throw new Error("Unauthorized company access");
    return { id: assetSnap.id, ...assetData };
};

const validateTransferEligibility = async (assetId, fromType, fromId, companyId) => {
    const asset = await validateAsset(assetId, companyId);
    const currentType = asset.current_holder_type;
    const currentId   = asset.current_holder_id;
    if (!currentType && !currentId) return true;
    if (currentType !== fromType || currentId !== fromId)
        throw new Error("Sender is not current holder");
    return true;
};

const enforceWorkflowTransition = ({
    workflowType, transactionClass, fromState, toState, context = "",
}) => {
    const result = validateWorkflowTransition({
        workflowType, transactionClass, fromState, toState,
    });
    if (!result.valid)
        throw new Error(`[${context}] Invalid workflow transition: ${result.reason}`);
};

const detectDuplicatePending = async (assetId, companyId) => {
    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("asset_id",       "==", assetId),
        where("company_id",     "==", companyId),
        where("workflow_state", "in", ACTIVE_PENDING_WORKFLOW_STATES)
    );
    const snap = await getDocs(q);
    if (!snap.empty) throw new Error("Asset already has an active pending workflow");
};

// =====================================================
// GET LATEST FINALIZED TRANSACTION — unchanged
// =====================================================

export const getLatestApprovedTransaction = async (assetId, companyId) => {
    validateCompany(companyId);
    const q = query(
        collection(db, ASSET_TRANSACTION_COLLECTION),
        where("asset_id",       "==", assetId),
        where("company_id",     "==", companyId),
        where("workflow_state", "==", WORKFLOW_STATES.FINALIZED),
        orderBy("created_at", "desc"),
        limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const latestDoc = snapshot.docs[0];
    return { id: latestDoc.id, ...latestDoc.data() };
};

// =====================================================
// CREATE TRANSFER REQUEST — unchanged
// =====================================================

export const createTransferRequest = async ({
    asset_id,
    from_type,
    from_id,
    from_holder_name = "",
    to_type,
    to_id,
    to_holder_name = "",
    to_uid = "",          // ← NEW: receiver ka guaranteed Firebase Auth UID
    requested_by,
    requested_by_name = "",
    remarks = "",
    company_id,
}) => {
    validateCompany(company_id);
    if (!asset_id)     throw new Error("asset_id is required");
    if (!requested_by) throw new Error("requested_by is required");
    if (!from_type || !to_type) throw new Error("Transfer types required");
    if (!from_id   || !to_id)   throw new Error("Transfer holders required");

    const asset = await validateAsset(asset_id, company_id);
    await validateTransferEligibility(asset_id, from_type, from_id, company_id);

    const workflow = resolveWorkflow({
        senderHolderType:   from_type,
        receiverHolderType: to_type,
    });

    if (!workflow.isFinalized) {
        await detectDuplicatePending(asset_id, company_id);
    }

    const fromHolderName = from_holder_name || asset.current_holder_name || "MAIN STORE";
    const toHolderName   = to_holder_name   || asset.assigned_to_name    || asset.current_holder_name || to_id;

    const transactionPayload = {
        asset_id,
        asset_name: asset.asset_name || "",
        serial_no:  asset.serial_no  || "",
        movement_type: workflow.transactionClass === TRANSACTION_CLASSES.RETURN
            ? MOVEMENT_TYPES.RETURN
            : MOVEMENT_TYPES.TRANSFER,
        from_type, from_id, to_type, to_id,
        from_holder_name: fromHolderName,
        to_holder_name:   toHolderName,
        from_snapshot: { holder_type: from_type, holder_id: from_id, holder_name: fromHolderName },
        to_snapshot:   { holder_type: to_type,   holder_id: to_id,   holder_name: toHolderName   },
        requested_by, requested_by_name,
        approved_by: null,
        remarks,
        company_id,
        // Sender handover tracking — false until sender confirms
        sender_handed_over:    false,
        sender_handed_over_at: null,
        status:            workflow.workflowState,
        workflow_type:     workflow.workflowType,
        workflow_state:    workflow.workflowState,
        transaction_class: workflow.transactionClass,
        visibility_bucket: workflow.visibilityBucket,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
    };

    const transactionRef = doc(collection(db, ASSET_TRANSACTION_COLLECTION));

    await runTransaction(db, async (writer) => {
        writer.set(transactionRef, transactionPayload);
        if (workflow.shouldMutateHolderImmediately) {
            const assetRef = doc(db, ASSET_MASTER_COLLECTION, asset_id);
            writer.update(assetRef, {
                current_holder_type: to_type,
                current_holder_id:   to_id,
                current_holder_name: toHolderName,
                updated_at: serverTimestamp(),
            });
        }
    });

    // ── NOTIFICATION — receiver always notified on transfer ──
    try {
        const isActionRequired = !workflow.isFinalized; // PENDING_ACCEPTANCE needs action
        await createNotification({
            type:               "asset_transfer",
            notification_class: isActionRequired ? "action_required" : "informational",
            title:              isActionRequired
                ? `Tool transfer request — ${asset.asset_name}`
                : `Tool assigned to you — ${asset.asset_name}`,
            body:               isActionRequired
                ? `${requested_by_name || "Someone"} wants to transfer ${asset.asset_name} to you. Please accept or reject.`
                : `${asset.asset_name} has been assigned to you by ${requested_by_name || "Store"}.`,
            route:              `/assets/pending`,
            for_uid:            to_uid || to_id,  // Auth UID prefer, fallback to_id
            company_id,
            reference_id:       transactionRef.id,
            reference_type:     "asset_transaction",
            priority:           isActionRequired ? "high" : "low",
        });
    } catch (notifErr) {
        console.warn("[assetTransactionService] transfer notification failed:", notifErr);
    }

    return {
        success:        true,
        transaction_id: transactionRef.id,
        status:         workflow.workflowState,
        workflow_type:  workflow.workflowType,
    };
};

// =====================================================
// CREATE RETURN REQUEST — unchanged
// =====================================================

export const createAssetReturnRequest = async ({
    asset_id,
    from_type,
    from_id,
    requested_by,
    requested_by_name = "",
    remarks = "",
    company_id,
}) => {
    validateCompany(company_id);
    if (!asset_id)     throw new Error("asset_id is required");
    if (!requested_by) throw new Error("requested_by is required");
    if (!from_type)    throw new Error("from_type is required");
    if (!from_id)      throw new Error("from_id is required");

    const asset = await validateAsset(asset_id, company_id);
    await validateTransferEligibility(asset_id, from_type, from_id, company_id);
    await detectDuplicatePending(asset_id, company_id);

    const fromHolderName = asset.current_holder_name || from_id;

    const transactionPayload = {
        asset_id,
        asset_name: asset.asset_name || "",
        serial_no:  asset.serial_no  || "",
        movement_type: MOVEMENT_TYPES.RETURN,
        from_type, from_id,
        to_type: HOLDER_TYPE.STORE,
        to_id:   "MAIN_STORE",
        from_holder_name: fromHolderName,
        to_holder_name:   "MAIN STORE",
        from_snapshot: { holder_type: from_type, holder_id: from_id, holder_name: fromHolderName },
        to_snapshot:   { holder_type: HOLDER_TYPE.STORE, holder_id: "MAIN_STORE", holder_name: "MAIN STORE" },
        requested_by, requested_by_name,
        approved_by: null,
        remarks,
        company_id,
        status:            WORKFLOW_STATES.RETURN_PENDING,
        workflow_type:     WORKFLOW_TYPES.RETURN_APPROVAL,
        workflow_state:    WORKFLOW_STATES.RETURN_PENDING,
        transaction_class: TRANSACTION_CLASSES.RETURN,
        visibility_bucket: WORKFLOW_BUCKETS.RECEIVE_RETURNS,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
    };

    const transactionRef = doc(collection(db, ASSET_TRANSACTION_COLLECTION));
    await setDoc(transactionRef, transactionPayload);

    // ── NOTIFICATION — Store/Manager notified of pending return ──
    try {
        await createNotification({
            type:               "asset_return",
            notification_class: "action_required",
            title:              `Tool return pending — ${asset.asset_name}`,
            body:               `${requested_by_name || "Staff"} has returned ${asset.asset_name}. Please confirm receipt.`,
            route:              `/assets/ledger?asset_id=${asset_id}`,
            for_roles:          ["store", "sm", "owner", "manager"],
            company_id,
            reference_id:       transactionRef.id,
            reference_type:     "asset_transaction",
            priority:           "medium",
        });
    } catch (notifErr) {
        console.warn("[assetTransactionService] return notification failed:", notifErr);
    }

    return { success: true, transaction_id: transactionRef.id };
};

// =====================================================
// CONFIRM HAND OVER — NEW
// =====================================================
/**
 * Sender confirms they have physically handed asset to receiver.
 * Does NOT change workflow_state — custody still PENDING_ACCEPTANCE.
 * Receiver must still call approveTransfer() to finalize custody.
 *
 * Sets: sender_handed_over: true, sender_handed_over_at: timestamp
 * UI shows "Waiting for <receiver> to accept" after this.
 */

export const confirmHandOver = async ({
    transaction_id,
    handed_over_by,
    company_id,
}) => {
    validateCompany(company_id);

    const transactionRef  = doc(db, ASSET_TRANSACTION_COLLECTION, transaction_id);
    const transactionSnap = await getDoc(transactionRef);

    if (!transactionSnap.exists()) throw new Error("Transaction not found");

    const transaction = transactionSnap.data();

    if (transaction.company_id !== company_id)
        throw new Error("Unauthorized company access");

    // Only valid on PENDING_ACCEPTANCE transactions
    if (transaction.workflow_state !== WORKFLOW_STATES.PENDING_ACCEPTANCE)
        throw new Error("Hand over only valid on pending acceptance transactions");

    // Only sender can confirm hand over
    if (transaction.from_id !== handed_over_by)
        throw new Error("Only the sender can confirm hand over");

    await runTransaction(db, async (writer) => {
        writer.update(transactionRef, {
            sender_handed_over:    true,
            sender_handed_over_at: serverTimestamp(),
            handed_over_by,
            updated_at:            serverTimestamp(),
        });
        // NO workflow_state change — custody pending until receiver accepts
        // NO asset_master update — holder unchanged
    });

    return { success: true };
};

// =====================================================
// APPROVE TRANSFER — unchanged
// PENDING_ACCEPTANCE → FINALIZED
// =====================================================

export const approveTransfer = async ({
    transaction_id,
    approved_by,
    company_id,
}) => {
    validateCompany(company_id);

    const transactionRef  = doc(db, ASSET_TRANSACTION_COLLECTION, transaction_id);
    const transactionSnap = await getDoc(transactionRef);
    if (!transactionSnap.exists()) throw new Error("Transaction not found");

    const transaction = transactionSnap.data();
    if (transaction.company_id !== company_id) throw new Error("Unauthorized company access");

    enforceWorkflowTransition({
        workflowType:     transaction.workflow_type,
        transactionClass: transaction.transaction_class,
        fromState:        transaction.workflow_state,
        toState:          WORKFLOW_STATES.FINALIZED,
        context:          "approveTransfer",
    });

    await runTransaction(db, async (writer) => {
        writer.update(transactionRef, {
            status:         WORKFLOW_STATES.FINALIZED,
            workflow_state: WORKFLOW_STATES.FINALIZED,
            approved_by,
            approved_at:    serverTimestamp(),
            updated_at:     serverTimestamp(),
        });
        const assetRef = doc(db, ASSET_MASTER_COLLECTION, transaction.asset_id);
        writer.update(assetRef, {
            current_holder_type: transaction.to_type,
            current_holder_id:   transaction.to_id,
            current_holder_name: transaction.to_holder_name || transaction.to_id,
            updated_at:          serverTimestamp(),
        });
    });

    // ── NOTIFICATION — sender informed receiver accepted ──
    // informational class — resolved/is_read not accepted by createNotification(),
    // always defaults false there, which is correct for informational anyway
    try {
        await createNotification({
            type:               "asset_transfer_accepted",
            notification_class: "informational",
            title:              `${transaction.to_holder_name || "Receiver"} accepted ${transaction.asset_name}`,
            body:               `Your transfer of ${transaction.asset_name} has been accepted.`,
            route:              `/assets/ledger?asset_id=${transaction.asset_id}`,
            for_uid:            transaction.requested_by,
            company_id,
            reference_id:       transaction_id,
            reference_type:     "asset_transaction",
            priority:           "low",
        });
    } catch (notifErr) {
        console.warn("[assetTransactionService] accept notification failed:", notifErr);
    }

    return { success: true };
};

// =====================================================
// RECEIVE RETURNED ASSET — unchanged
// RETURN_PENDING → FINALIZED
// =====================================================

export const receiveReturnedAsset = async ({
    transaction_id,
    received_by,
    company_id,
}) => {
    validateCompany(company_id);

    const transactionRef  = doc(db, ASSET_TRANSACTION_COLLECTION, transaction_id);
    const transactionSnap = await getDoc(transactionRef);
    if (!transactionSnap.exists()) throw new Error("Return request not found");

    const transaction = transactionSnap.data();
    if (transaction.company_id !== company_id) throw new Error("Unauthorized company access");

    enforceWorkflowTransition({
        workflowType:     transaction.workflow_type,
        transactionClass: transaction.transaction_class,
        fromState:        transaction.workflow_state,
        toState:          WORKFLOW_STATES.FINALIZED,
        context:          "receiveReturnedAsset",
    });

    await runTransaction(db, async (writer) => {
        writer.update(transactionRef, {
            status:         WORKFLOW_STATES.FINALIZED,
            workflow_state: WORKFLOW_STATES.FINALIZED,
            received_by,
            received_at:    serverTimestamp(),
            updated_at:     serverTimestamp(),
        });
        const assetRef = doc(db, ASSET_MASTER_COLLECTION, transaction.asset_id);
        writer.update(assetRef, {
            current_holder_type: HOLDER_TYPE.STORE,
            current_holder_id:   "MAIN_STORE",
            current_holder_name: "MAIN STORE",
            updated_at:          serverTimestamp(),
        });
    });

    return { success: true };
};

// =====================================================
// REJECT TRANSFER — unchanged
// PENDING_ACCEPTANCE → REJECTED
// =====================================================

export const rejectTransfer = async ({
    transaction_id,
    rejected_by,
    rejection_reason = "",
    company_id,
}) => {
    validateCompany(company_id);

    const transactionRef  = doc(db, ASSET_TRANSACTION_COLLECTION, transaction_id);
    const transactionSnap = await getDoc(transactionRef);
    if (!transactionSnap.exists()) throw new Error("Transaction not found");

    const transaction = transactionSnap.data();
    if (transaction.company_id !== company_id) throw new Error("Unauthorized company access");

    enforceWorkflowTransition({
        workflowType:     transaction.workflow_type,
        transactionClass: transaction.transaction_class,
        fromState:        transaction.workflow_state,
        toState:          WORKFLOW_STATES.REJECTED,
        context:          "rejectTransfer",
    });

    await runTransaction(db, async (writer) => {
        writer.update(transactionRef, {
            status:           WORKFLOW_STATES.REJECTED,
            workflow_state:   WORKFLOW_STATES.REJECTED,
            rejected_by,
            rejection_reason,
            rejected_at:      serverTimestamp(),
            updated_at:       serverTimestamp(),
        });
        // NO holder mutation — custody stays with sender
    });

    return { success: true };
};

// =====================================================
// CANCEL TRANSFER — ADDITIVE CHANGE
// PENDING_ACCEPTANCE → CANCELLED
// RETURN_PENDING     → CANCELLED
//
// ADDED: cancellation_reason (optional at service layer)
//        cancelled_by_role   ("sender" | "manager" — for audit)
// =====================================================

export const cancelTransfer = async ({
    transaction_id,
    cancelled_by,
    cancellation_reason = "",   // ← ADDED (optional, UI enforces mandatory)
    cancelled_by_role   = "",   // ← ADDED ("sender" | "manager" for audit trail)
    company_id,
}) => {
    validateCompany(company_id);

    const transactionRef  = doc(db, ASSET_TRANSACTION_COLLECTION, transaction_id);
    const transactionSnap = await getDoc(transactionRef);
    if (!transactionSnap.exists()) throw new Error("Transaction not found");

    const transaction = transactionSnap.data();
    if (transaction.company_id !== company_id) throw new Error("Unauthorized company access");

    enforceWorkflowTransition({
        workflowType:     transaction.workflow_type,
        transactionClass: transaction.transaction_class,
        fromState:        transaction.workflow_state,
        toState:          WORKFLOW_STATES.CANCELLED,
        context:          "cancelTransfer",
    });

    await runTransaction(db, async (writer) => {
        writer.update(transactionRef, {
            status:              WORKFLOW_STATES.CANCELLED,
            workflow_state:      WORKFLOW_STATES.CANCELLED,
            cancelled_by,
            cancellation_reason,                            // ← ADDED
            cancelled_by_role,                              // ← ADDED
            cancelled_at:        serverTimestamp(),
            updated_at:          serverTimestamp(),
        });
        // NO holder mutation — custody stays with sender
    });

    return { success: true };
};